/**
 * @file task-permissions.ts
 * @description Кто что может с задачей (phase19.31, crm.md T3–T4) — чтобы
 * кабинет не показывал кнопку, на которую сервер ответит отказом.
 *
 *  - отметить выполненной — исполнитель, автор или владелец магазина (T4);
 *  - вернуть в работу — те же, у выполненного дела (контракт §13);
 *  - назначить/сменить исполнителя — любой сотрудник с правом CRM (T3);
 *  - удалить — только автор (прежнее правило сервера).
 *
 * Последнее слово всё равно за сервером: при отказе кнопка покажет его
 * текст, а список перечитается.
 */

import type { ActivityDto } from "../../../lib/api-crm";
import { TASK_KINDS, type TaskKind } from "./tasks-query";

/** Кто смотрит на задачу. */
export interface TaskViewer {
  userId: string | null;
  /** Владелец магазина — суперроль кабинета магазина. */
  isShopOwner: boolean;
  /** Право работы с клиентами и сделками (`SHOP_USE_CRM`). */
  canUseCrm: boolean;
}

type TaskInput = Pick<ActivityDto, "kind" | "completedAt" | "assigneeUserId" | "createdById">;

function isTaskKind(kind: ActivityDto["kind"]): kind is TaskKind {
  return (TASK_KINDS as readonly string[]).includes(kind);
}

/** T4: исполнитель, автор или владелец; только у открытого дела. */
export function canCompleteTask(task: TaskInput, viewer: TaskViewer): boolean {
  if (!viewer.canUseCrm || task.completedAt || !isTaskKind(task.kind)) return false;
  if (viewer.isShopOwner) return true;
  return viewer.userId !== null && (task.assigneeUserId === viewer.userId || task.createdById === viewer.userId);
}

/**
 * «Вернуть в работу» (контракт §13): те же, кто может отметить выполненной
 * (исполнитель, автор, владелец), и только у выполненного дела.
 */
export function canReopenTask(task: TaskInput, viewer: TaskViewer): boolean {
  if (!viewer.canUseCrm || !task.completedAt || !isTaskKind(task.kind)) return false;
  if (viewer.isShopOwner) return true;
  return viewer.userId !== null && (task.assigneeUserId === viewer.userId || task.createdById === viewer.userId);
}

/** T3: любой сотрудник с правом CRM; у выполненного дела исполнителя не меняют. */
export function canAssignTask(task: TaskInput, viewer: TaskViewer): boolean {
  return viewer.canUseCrm && !task.completedAt && isTaskKind(task.kind);
}

/** Удалить может только автор. */
export function canDeleteTask(task: TaskInput, viewer: TaskViewer): boolean {
  return viewer.userId !== null && task.createdById === viewer.userId;
}
